import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { APP_BASE_PATH, AppRoutes } from '@/constants';
import { usePermission } from '@/hooks';
import { ProvidersLayout } from '@/providers';
import { getUserDefaultHomePage } from '@/utils';
import { AuthAction, Module, SubModule } from '@/enums';
import {
  ForgotPasswordPage,
  InitialPasswordPage,
  InternalErrorPage,
  LoginPage,
  NoPermissionPage,
  NotFoundPage,
  PasswordResetPage,
  RegulatoryIntakeDetailsPage,
  RegulatoryIntakeListPage,
  UserManagementCreationPage,
  UserManagementListPage,
  UserManagementUpdatePage,
} from '@/pages';

import { AuthLayout, ErrorLayout, MainLayout } from '@/components/layouts';
import { Loading, ProtectedRoute } from '@/components/common';

const RoleBasedHomeRedirect = () => {
  const { roles, isLoading } = usePermission();

  if (isLoading) return <Loading fullScreen />;

  return <Navigate to={getUserDefaultHomePage(roles)} />;
};

const appRoutes = [
  {
    element: <MainLayout />,
    children: [
      {
        path: AppRoutes.HOME,
        element: <RoleBasedHomeRedirect />,
      },
      {
        path: AppRoutes.INTAKE_JOBS,
        element: (
          <ProtectedRoute
            permissions={[
              {
                module: Module.RegulatoryIntake,
                subModule: SubModule.RegulatoryIntake,
                action: AuthAction.Read,
              },
            ]}
          >
            <RegulatoryIntakeListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: AppRoutes.REGULATORY_INTAKE_DETAILS,
        element: (
          <ProtectedRoute
            permissions={[
              {
                module: Module.RegulatoryIntake,
                subModule: SubModule.RegulatoryIntake,
                action: AuthAction.Update,
              },
            ]}
          >
            <RegulatoryIntakeDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: AppRoutes.USER_MANAGEMENT,
        element: (
          <ProtectedRoute
            permissions={[
              {
                module: Module.UserManagement,
                subModule: SubModule.UserManagement,
                action: AuthAction.Read,
              },
            ]}
          >
            <UserManagementListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: AppRoutes.USER_MANAGEMENT_CREATION,
        element: (
          <ProtectedRoute
            permissions={[
              {
                module: Module.UserManagement,
                subModule: SubModule.UserManagement,
                action: AuthAction.Create,
              },
            ]}
          >
            <UserManagementCreationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: AppRoutes.USER_MANAGEMENT_UPDATE,
        element: (
          <ProtectedRoute
            permissions={[
              {
                module: Module.UserManagement,
                subModule: SubModule.UserManagement,
                action: AuthAction.Update,
              },
            ]}
          >
            <UserManagementUpdatePage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: AppRoutes.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
      { path: AppRoutes.LOGIN, element: <LoginPage /> },
      { path: AppRoutes.INITIAL_PASSWORD, element: <InitialPasswordPage /> },
      { path: AppRoutes.PASSWORD_RESET, element: <PasswordResetPage /> },
    ],
  },
  {
    element: <ErrorLayout />,
    children: [
      { path: AppRoutes.FORBIDDEN, element: <NoPermissionPage /> },
      { path: AppRoutes.INTERNAL_SERVER_ERROR, element: <InternalErrorPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(
  [
    {
      element: <ProvidersLayout />,
      children: appRoutes,
    },
  ],
  {
    basename: APP_BASE_PATH,
  },
);

export const App = () => (
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
